import { useState } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
      
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 5000);
    }, 1500);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <section id="contact-section" className="py-32 bg-gradient-to-b from-purple-50 to-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-5xl font-bold text-gray-900 mb-4">つながって創造しましょう</h2>
          <p className="text-lg text-gray-600">あなたの<span className="san-brainy">VTuber</span>ビジョンを実現する準備はできていますか？お話ししましょう！</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Left: Contact Form */}
          <div className="bg-white rounded-3xl shadow-xl p-10">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">メッセージを送る</h3>
            
            {submitStatus === 'success' && (
              <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
                <i className="ri-checkbox-circle-fill text-2xl text-green-500"></i>
                <p className="text-sm text-green-700">お問い合わせありがとうございます！すぐにご連絡いたします。</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  お名前
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all"
                  placeholder="山田太郎"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  メールアドレス
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all"
                  placeholder="yamada@example.com"
                />
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                  件名
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all"
                  placeholder="プロジェクトのお問い合わせ"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                  メッセージ
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent transition-all resize-none"
                  placeholder="プロジェクトについて教えてください..."
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-semibold rounded-xl shadow-lg hover:scale-105 transition-transform duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? '送信中...' : 'メッセージを送信'}
              </button>
            </form>
          </div>

          {/* Right: Contact Info & Links */}
          <div className="space-y-8">
            {/* Direct Contact */}
            <div className="bg-gradient-to-br from-pink-500 to-purple-600 rounded-3xl shadow-xl p-10 text-white">
              <h3 className="text-2xl font-bold mb-6">直接連絡</h3>
              
              <div className="space-y-4">
                <a
                  href="mailto:dlmunera93@gmail.com"
                  className="flex items-center gap-4 p-4 bg-white/10 backdrop-blur-sm rounded-xl hover:bg-white/20 transition-all cursor-pointer"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg">
                    <i className="ri-mail-line text-xl"></i>
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-wide opacity-80">メール</div>
                    <div className="font-semibold">dlmunera93@gmail.com</div>
                  </div>
                </a>
              </div>
            </div>

            {/* Professional Links */}
            <div className="bg-white rounded-3xl shadow-xl p-10">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">プロフェッショナルリンク</h3>
              
              <div className="space-y-3">
                <a
                  href="https://www.linkedin.com/in/daniellozanomunera/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-pink-500 hover:bg-pink-50 transition-all cursor-pointer group"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-100 group-hover:bg-pink-500 rounded-lg transition-colors">
                    <i className="ri-linkedin-fill text-xl text-gray-600 group-hover:text-white"></i>
                  </div>
                  <span className="font-medium text-gray-700 group-hover:text-pink-600">LinkedInプロフィール</span>
                </a>

                <a
                  href="https://www.behance.net/daniellozano25"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-pink-500 hover:bg-pink-50 transition-all cursor-pointer group"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-100 group-hover:bg-pink-500 rounded-lg transition-colors">
                    <i className="ri-behance-fill text-xl text-gray-600 group-hover:text-white"></i>
                  </div>
                  <span className="font-medium text-gray-700 group-hover:text-pink-600">Behanceポートフォリオ</span>
                </a>

                <a
                  href="https://drive.google.com/file/d/1J6oEzLRLCK7m5uqjEyqe2l7PbaoHZ6N3/view?usp=sharing"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 border border-gray-200 rounded-xl hover:border-pink-500 hover:bg-pink-50 transition-all cursor-pointer group"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-100 group-hover:bg-pink-500 rounded-lg transition-colors">
                    <i className="ri-file-download-line text-xl text-gray-600 group-hover:text-white"></i>
                  </div>
                  <span className="font-medium text-gray-700 group-hover:text-pink-600">CVをダウンロード</span>
                </a>
              </div>
            </div>

            {/* Hiring Opportunities */}
            <div className="bg-gradient-to-br from-cyan-500 to-teal-500 rounded-3xl shadow-xl p-10 text-white">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
                  <i className="ri-briefcase-line text-2xl"></i>
                </div>
                <div>
                  <h3 className="text-2xl font-bold mb-2">お仕事募集中</h3>
                  <p className="text-sm text-white/90 leading-relaxed mb-4">
                    現在、フリーランスプロジェクト、契約業務、VTuberおよびエンターテインメント業界でのフルタイムポジションを募集しています。
                  </p>
                </div>
              </div>
              
              <div className="space-y-3 pl-16">
                <div className="flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-lg"></i>
                  <span className="text-sm font-medium">リモートワーク</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-lg"></i>
                  <span className="text-sm font-medium">転居可能</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-lg"></i>
                  <span className="text-sm font-medium">フリーランスプロジェクト</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-checkbox-circle-fill text-lg"></i>
                  <span className="text-sm font-medium">契約ポジション</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}